import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Clock, Play, Square, Coffee, Timer, ArrowRight } from 'lucide-react';

export const TimeTrackingWidget = ({ timeStatus, actionLoading, onAction }) => {
  const navigate = useNavigate();

  return (
    <Card className="relative bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
      <CardContent className="p-6">
        {/* Persistent nav link – always top-right, never shifts */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/zeiterfassung')}
          className="absolute top-3 right-3 h-8 gap-1 text-xs text-muted-foreground hover:text-foreground"
          data-testid="goto-zeiterfassung-btn"
        >
          <span className="hidden sm:inline">Zur Zeiterfassung</span>
          <span className="sm:hidden">Übersicht</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pr-0 sm:pr-32">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <Clock className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Zeiterfassung</h3>
              {timeStatus ? (
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge
                    variant={timeStatus.status === 'clocked_in' ? 'default' : timeStatus.status === 'on_break' ? 'secondary' : 'outline'}
                    className={timeStatus.status === 'on_break' ? 'bg-amber-500 text-white' : ''}
                  >
                    {{
                      'clocked_in': 'Eingestempelt',
                      'on_break': 'In Pause',
                      'clocked_out': 'Ausgestempelt',
                    }[timeStatus.status] || 'Nicht eingestempelt'}
                  </Badge>
                  {timeStatus.status === 'clocked_in' && timeStatus.working_time && (
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Timer className="h-4 w-4" />
                      {Math.floor(timeStatus.working_time / 60)}:{(timeStatus.working_time % 60).toString().padStart(2, '0')} Std
                    </span>
                  )}
                  {timeStatus.status === 'on_break' && (
                    <span className="text-sm text-amber-600 flex items-center gap-1">
                      <Coffee className="h-4 w-4" />
                      Pause läuft...
                    </span>
                  )}
                  {timeStatus.clock_in && timeStatus.status !== 'on_break' && (
                    <span className="text-sm text-muted-foreground">
                      seit {new Date(timeStatus.clock_in).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mt-1">Status wird geladen...</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {(!timeStatus || timeStatus.status === 'not_clocked_in' || timeStatus.status === 'clocked_out') && (
              <Button onClick={() => onAction('clock-in')} disabled={actionLoading} className="gap-2">
                <Play className="h-4 w-4" />Jetzt einstempeln
              </Button>
            )}
            {timeStatus?.status === 'clocked_in' && (
              <>
                <Button variant="secondary" onClick={() => onAction('break-start')} disabled={actionLoading} className="gap-2" data-testid="break-start-btn">
                  <Coffee className="h-4 w-4" />Pause starten
                </Button>
                <Button variant="destructive" onClick={() => onAction('clock-out')} disabled={actionLoading} className="gap-2" data-testid="clock-out-btn">
                  <Square className="h-4 w-4" />Ausstempeln
                </Button>
              </>
            )}
            {timeStatus?.status === 'on_break' && (
              <Button variant="default" onClick={() => onAction('break-end')} disabled={actionLoading} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="break-end-btn">
                <Coffee className="h-4 w-4" />Pause beendet
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
